import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MagnifyingGlass, Globe, Buildings, Users, Briefcase, ArrowRight, Download, Cube, Funnel, MapPin } from "@phosphor-icons/react";

const SECTORS = [
  "Education & Childcare", "Healthcare", "IT & Technology",
  "Finance & Professional Services", "Engineering & Construction",
];

const FILTER_TAGS = [
  { value: "", label: "All Records" },
  { value: "recently_contacted", label: "Recently Contacted" },
  { value: "existing_client", label: "Existing Client" },
  { value: "prospect", label: "Prospect" },
];

export default function SearchCentrePage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [sector, setSector] = useState("");
  const [employerType, setEmployerType] = useState("");
  const [roleType, setRoleType] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("search");
  const [showFilters, setShowFilters] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim() && !location && !sector && !employerType && !roleType && !filterTag) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set("q", searchQuery);
      if (location) params.set("location", location);
      if (sector) params.set("sector", sector);
      if (employerType) params.set("employer_type", employerType);
      if (roleType) params.set("role_type", roleType);
      if (filterTag) params.set("filter_tag", filterTag);
      const { data } = await api.get(`/search?${params.toString()}`);
      setResults(data);
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) { toast.error("Enter a URL"); return; }
    setImportLoading(true);
    try {
      const { data } = await api.post("/search/import-website", { url: importUrl });
      setImportResult(data);
      toast.success("Website data extracted");
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImportLoading(false); }
  };

  const createCompanyFromImport = async () => {
    if (!importResult) return;
    try {
      await api.post("/companies", {
        name: importResult.title || importResult.url,
        email: importResult.emails?.[0] || "",
        phone: importResult.phones?.[0] || "",
        website: importResult.url,
        domain: new URL(importResult.url).hostname,
        notes: importResult.description || "",
      });
      toast.success("Company created from website data");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create company"); }
  };

  const totalResults = results ? Object.values(results).reduce((acc, arr) => acc + (arr?.length || 0), 0) : 0;
  const ENTITY_ICONS = { companies: Buildings, candidates: Users, vacancies: Cube, leads: MagnifyingGlass };
  const activeFilters = [location, sector, employerType, roleType, filterTag].filter(Boolean).length;

  return (
    <div data-testid="search-centre-page" className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Intelligent Search</h1>
        <p className="text-sm text-[#6B7280] mt-0.5">Search CRM records with advanced filters and import website data</p>
      </div>

      <div className="flex gap-1 bg-[#F3F4F6] p-1 rounded-lg w-fit">
        <button onClick={() => setActiveTab("search")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "search" ? "bg-white text-[#0F172A] shadow-sm" : "text-[#6B7280]"}`} data-testid="tab-search">
          <MagnifyingGlass size={16} className="inline mr-1" /> CRM Search
        </button>
        <button onClick={() => setActiveTab("import")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "import" ? "bg-white text-[#0F172A] shadow-sm" : "text-[#6B7280]"}`} data-testid="tab-import">
          <Globe size={16} className="inline mr-1" /> Website Import
        </button>
      </div>

      {activeTab === "search" ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Search companies, candidates, vacancies, leads..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()}
              className="flex-1" data-testid="search-input" />
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className="gap-2 relative" data-testid="toggle-filters-btn">
              <Funnel size={16} /> Filters
              {activeFilters > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#C5A059] text-white text-[9px] flex items-center justify-center">{activeFilters}</span>
              )}
            </Button>
            <Button onClick={handleSearch} disabled={loading} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="search-btn">
              <MagnifyingGlass size={16} /> Search
            </Button>
          </div>

          {showFilters && (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="search-filters-panel">
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block flex items-center gap-1"><MapPin size={12} /> Location</label>
                <Input placeholder="Town, City or County" value={location} onChange={e => setLocation(e.target.value)}
                  className="h-9 text-sm" data-testid="filter-location" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Sector</label>
                <Select value={sector} onValueChange={setSector}>
                  <SelectTrigger className="h-9 text-sm" data-testid="filter-sector"><SelectValue placeholder="All Sectors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">All Sectors</SelectItem>
                    {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Employer Type</label>
                <Input placeholder="e.g. Care Homes" value={employerType} onChange={e => setEmployerType(e.target.value)}
                  className="h-9 text-sm" data-testid="filter-employer-type" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Role Type</label>
                <Input placeholder="e.g. Software Developer" value={roleType} onChange={e => setRoleType(e.target.value)}
                  className="h-9 text-sm" data-testid="filter-role-type" />
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Status</label>
                <Select value={filterTag} onValueChange={setFilterTag}>
                  <SelectTrigger className="h-9 text-sm" data-testid="filter-status"><SelectValue placeholder="All Records" /></SelectTrigger>
                  <SelectContent>
                    {FILTER_TAGS.map(f => <SelectItem key={f.value || "all"} value={f.value || " "}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 lg:col-span-5 flex justify-end gap-2">
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setLocation(""); setSector(""); setEmployerType(""); setRoleType(""); setFilterTag(""); }}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 skeleton-shimmer rounded-lg" />)}</div>
          ) : results ? (
            <div className="space-y-4">
              <p className="text-sm text-[#6B7280]">{totalResults} result{totalResults !== 1 ? "s" : ""} found</p>
              {Object.entries(results).map(([type, items]) => {
                if (!items || items.length === 0) return null;
                const Icon = ENTITY_ICONS[type] || MagnifyingGlass;
                return (
                  <div key={type}>
                    <h3 className="text-sm font-semibold text-[#0F172A] uppercase mb-2 flex items-center gap-2">
                      <Icon size={16} /> {type} ({items.length})
                    </h3>
                    <div className="space-y-1">
                      {items.map((item, i) => {
                        const displayName = item.name || item.title || item.company_name ||
                          (item.first_name ? `${item.first_name} ${item.last_name || ""}`.trim() : "") ||
                          item.opportunity || "Unnamed";
                        const subtitle = type === "leads"
                          ? [item.opportunity, item.sector, item.status].filter(Boolean).join(" · ")
                          : (item.email || item.location || item.status || item.sector || "");
                        const navPath = type === "companies" ? "/companies" : type === "candidates" ? "/candidates" : type === "vacancies" ? "/vacancies" : type === "leads" ? "/leads" : type === "users" ? "/users" : null;
                        return (
                          <div key={item.id || i} role={navPath ? "button" : undefined} tabIndex={navPath ? 0 : undefined}
                            onClick={() => navPath && (window.location.href = navPath)}
                            className={`bg-white rounded-lg border border-[#E5E7EB] px-4 py-3 flex items-center justify-between ${navPath ? "cursor-pointer hover:bg-[#F9FAFB]" : ""}`}
                            data-testid={`search-result-${type}-${i}`}>
                            <div>
                              <p className="text-sm font-medium text-[#0F172A]">{displayName}</p>
                              <p className="text-xs text-[#6B7280]">{subtitle}</p>
                            </div>
                            {navPath && <ArrowRight size={16} className="text-[#C5A059]" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
              <MagnifyingGlass size={48} className="text-[#D1D5DB] mx-auto mb-3" />
              <p className="text-[#6B7280]">Search across your CRM using keywords and advanced filters</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Enter website URL (e.g. https://example.com)" value={importUrl}
              onChange={e => setImportUrl(e.target.value)} className="flex-1" data-testid="import-url-input" />
            <Button onClick={handleImport} disabled={importLoading} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="import-btn">
              <Download size={16} /> {importLoading ? "Importing..." : "Import"}
            </Button>
          </div>

          {importResult && (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-5 space-y-3" data-testid="import-result">
              <h3 className="font-semibold text-[#0F172A]">{importResult.title || importResult.url}</h3>
              {importResult.description && <p className="text-sm text-[#6B7280]">{importResult.description}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {importResult.emails?.length > 0 && (
                  <div className="p-3 bg-[#F9FAFB] rounded-lg">
                    <p className="text-xs text-[#6B7280] font-medium mb-1">Emails Found</p>
                    {importResult.emails.map((e, i) => <p key={i} className="text-sm text-[#0F172A]">{e}</p>)}
                  </div>
                )}
                {importResult.phones?.length > 0 && (
                  <div className="p-3 bg-[#F9FAFB] rounded-lg">
                    <p className="text-xs text-[#6B7280] font-medium mb-1">Phone Numbers Found</p>
                    {importResult.phones.map((p, i) => <p key={i} className="text-sm text-[#0F172A]">{p}</p>)}
                  </div>
                )}
                {importResult.careers_page && (
                  <div className="p-3 bg-[#F9FAFB] rounded-lg">
                    <p className="text-xs text-[#6B7280] font-medium mb-1">Careers Page</p>
                    <a href={importResult.careers_page} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1E3A8A] hover:underline">{importResult.careers_page}</a>
                  </div>
                )}
                {importResult.contact_page && (
                  <div className="p-3 bg-[#F9FAFB] rounded-lg">
                    <p className="text-xs text-[#6B7280] font-medium mb-1">Contact Page</p>
                    <a href={importResult.contact_page} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1E3A8A] hover:underline">{importResult.contact_page}</a>
                  </div>
                )}
              </div>
              {Object.keys(importResult.social_links || {}).length > 0 && (
                <div className="p-3 bg-[#F9FAFB] rounded-lg">
                  <p className="text-xs text-[#6B7280] font-medium mb-1">Social Links</p>
                  {Object.entries(importResult.social_links).map(([k, v]) => (
                    <p key={k}><a href={v} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1E3A8A] hover:underline">{k}: {v}</a></p>
                  ))}
                </div>
              )}
              <Button onClick={createCompanyFromImport} className="bg-[#C5A059] hover:bg-[#B8933F] text-white gap-2" data-testid="create-company-from-import-btn">
                <Buildings size={16} /> Create Company from Data
              </Button>
            </div>
          )}

          {!importResult && !importLoading && (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
              <Globe size={48} className="text-[#D1D5DB] mx-auto mb-3" />
              <p className="text-[#6B7280]">Enter a website URL to extract company data, emails, careers page and social links</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
